import { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Package } from '@phosphor-icons/react';
import { Alert, EmptyState, Page, PageHeader, Panel, SkeletonRows, Stat, Stats, TableWrap } from 'design';
import './SealedVault.css';

function getStoredUser() {
  try {
    const raw = localStorage.getItem('user');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function money(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return '£0';
  return `£${numeric.toLocaleString('en-GB', { maximumFractionDigits: 0 })}`;
}

function unitValue(item) {
  const override = Number(item.price_override);
  if (Number.isFinite(override) && override > 0) return override;
  const market = Number(item.market_price);
  return Number.isFinite(market) ? market : 0;
}

const blankForm = {
  product_name: '',
  set_name: '',
  product_type: '',
  quantity: '1',
  average_cost_paid: '',
  market_price: '',
  price_override: '',
  notes: '',
};

const FIELDS = [
  { key: 'product_name', label: 'Product', placeholder: 'Product' },
  { key: 'set_name', label: 'Set', placeholder: 'Set' },
  { key: 'product_type', label: 'Type', placeholder: 'Type' },
  { key: 'quantity', label: 'Quantity', placeholder: 'Qty', type: 'number', min: '1' },
  { key: 'average_cost_paid', label: 'Unit cost', placeholder: 'Unit cost', inputMode: 'decimal' },
  { key: 'market_price', label: 'Unit market price', placeholder: 'Unit market', inputMode: 'decimal' },
  { key: 'price_override', label: 'Price override', placeholder: 'Override', inputMode: 'decimal' },
];

export default function SealedVault() {
  const session = getStoredUser();
  const email = String(session?.email || '').toLowerCase();
  const allowed = email.endsWith('@pokerand.net') || email.endsWith('@randcards.com');
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(blankForm);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('value_desc');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null);

  useEffect(() => {
    if (!session || !allowed) return;
    fetch('/api/sealed-vault/items', {
      headers: { Authorization: `Bearer ${session.accessToken}` },
    })
      .then(async (res) => {
        const body = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(body.error || `HTTP ${res.status}`);
        setItems(Array.isArray(body.items) ? body.items : []);
      })
      .catch((err) => setStatus({ type: 'error', text: err.message || 'Failed to load sealed vault' }))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items
      .filter((item) => !q || [item.product_name, item.set_name, item.product_type].join(' ').toLowerCase().includes(q))
      .sort((a, b) => {
        if (sort === 'name') return String(a.product_name).localeCompare(String(b.product_name));
        if (sort === 'qty_desc') return Number(b.quantity || 0) - Number(a.quantity || 0);
        return unitValue(b) * Number(b.quantity || 0) - unitValue(a) * Number(a.quantity || 0);
      });
  }, [items, search, sort]);

  const summary = useMemo(() => items.reduce((acc, item) => {
    const quantity = Number(item.quantity || 0);
    const cost = Number(item.average_cost_paid || 0) * quantity;
    const value = unitValue(item) * quantity;
    acc.units += quantity;
    acc.cost += cost;
    acc.value += value;
    acc.profit += value - cost;
    return acc;
  }, { units: 0, cost: 0, value: 0, profit: 0 }), [items]);

  if (!session) return <Navigate to="/login" replace />;
  if (!allowed) return <Navigate to="/" replace />;

  async function addItem(event) {
    event.preventDefault();
    if (saving) return;
    setSaving(true);
    setStatus(null);

    try {
      const res = await fetch('/api/sealed-vault/items', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session.accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(form),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || `HTTP ${res.status}`);
      setItems((current) => [body.item, ...current]);
      setForm(blankForm);
      setStatus({ type: 'success', text: `Added ${body.item.product_name}` });
    } catch (err) {
      setStatus({ type: 'error', text: err.message || 'Failed to add sealed item' });
    } finally {
      setSaving(false);
    }
  }

  async function removeItem(id) {
    if (!id) return;
    const res = await fetch(`/api/sealed-vault/${id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${session.accessToken}` },
    });
    if (res.ok) {
      setItems((current) => current.filter((item) => item.id !== id));
    }
  }

  const gainClass = (amount) => (amount >= 0 ? 'svlt-gain' : 'svlt-loss');

  return (
    <Page>
      <PageHeader
        title="Sealed vault"
        description="A separate tracker for sealed product held for the personal collection or as an investment."
      />

      {status ? <Alert tone={status.type === 'error' ? 'error' : 'success'}>{status.text}</Alert> : null}

      <Stats>
        <Stat label="Market value" value={money(summary.value)} />
        <Stat label="Cost" value={money(summary.cost)} />
        <Stat label="Unrealised P&L" value={<span className={gainClass(summary.profit)}>{money(summary.profit)}</span>} />
        <Stat label="Units" value={summary.units.toLocaleString('en-GB')} />
      </Stats>

      <Panel as="form" title="Add sealed product" onSubmit={addItem}>
        <div className="svlt-form">
          {FIELDS.map((field) => (
            <label key={field.key} className="rc-field">
              <span className="rc-label">{field.label}</span>
              <input
                placeholder={field.placeholder}
                type={field.type}
                min={field.min}
                inputMode={field.inputMode}
                value={form[field.key]}
                onChange={(e) => setForm({ ...form, [field.key]: e.target.value })}
              />
            </label>
          ))}
          <button className="rc-btn rc-btn--primary svlt-add" type="submit">{saving ? 'Adding...' : 'Add item'}</button>
        </div>
      </Panel>

      <div className="rc-toolbar">
        <label className="rc-input rc-toolbar__grow">
          <span className="rc-sr-only">Search sealed product</span>
          <input placeholder="Search sealed..." value={search} onChange={(e) => setSearch(e.target.value)} />
        </label>
        <label className="svlt-sort">
          <span className="rc-sr-only">Sort</span>
          <select value={sort} onChange={(e) => setSort(e.target.value)}>
            <option value="value_desc">Value high first</option>
            <option value="qty_desc">Quantity high first</option>
            <option value="name">Name A-Z</option>
          </select>
        </label>
      </div>

      {loading ? <Panel><SkeletonRows rows={4} label="Loading sealed vault" /></Panel> : null}
      {!loading && filtered.length === 0 ? (
        <EmptyState icon={Package} title={items.length ? 'Nothing matches that search' : 'No sealed product yet'}>
          {items.length ? 'Try a product, set or type name.' : 'Add your first box or collection above and it will show here with its value.'}
        </EmptyState>
      ) : null}
      {!loading && filtered.length ? (
        <Panel flush>
          <TableWrap>
            <table className="rc-table rc-table--stack svlt-table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th className="rc-num">Qty</th>
                  <th className="rc-num">Unit</th>
                  <th className="rc-num">Value</th>
                  <th className="rc-num">P&amp;L</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {filtered.map((item) => {
                  const quantity = Number(item.quantity || 0);
                  const cost = Number(item.average_cost_paid || 0) * quantity;
                  const value = unitValue(item) * quantity;
                  return (
                    <tr key={item.id}>
                      <td>
                        <span className="svlt-product">{item.product_name}</span>
                        <span className="svlt-meta">{[item.set_name, item.product_type].filter(Boolean).join(' • ') || 'Sealed product'}</span>
                      </td>
                      <td className="rc-num" data-label="Qty">{quantity.toLocaleString('en-GB')}</td>
                      <td className="rc-num" data-label="Unit">{money(unitValue(item))}</td>
                      <td className="rc-num" data-label="Value">{money(value)}</td>
                      <td className={`rc-num ${gainClass(value - cost)}`} data-label="P&L">{money(value - cost)}</td>
                      <td className="svlt-actions"><button className="rc-btn rc-btn--sm" type="button" onClick={() => removeItem(item.id)}>Remove</button></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </TableWrap>
        </Panel>
      ) : null}
    </Page>
  );
}
